import classNames from "classnames";
import React from "react";
import SVG from "react-inlinesvg";
import { c, msgid, ngettext } from "ttag";

import iconStarHalfOutline from "../../img/icons/star-half-outline.svg";
import iconStarOutline from "../../img/icons/star-outline.svg";
import iconStarWithStroke from "../../img/icons/star-with-stroke.svg";
import iconStar from "../../img/icons/star.svg";
import { formatNumber } from "../utils/format";
import { range } from "../utils/functional";
import { modulo } from "../utils/math";

import styles from "./RatingGraphic.module.scss";

const HALF_STAR_MIN = 0.25;
const HALF_STAR_MAX = 0.75;

interface StarSVGInfo {
    svgPath: string;
    halfOutline: boolean;
    outline: boolean;
}

const buildLinearGradient = (): React.ReactNode => {
    const style = {
        width: "0",
        height: "0",
    };

    return (
        <svg aria-hidden="true" style={style}>
            <linearGradient id="halfGradient">
                <stop
                    className="star-rating__star--half-star-color"
                    stopOpacity="1"
                    offset="50%"
                ></stop>
                <stop stopOpacity="0" offset="50%"></stop>
            </linearGradient>
        </svg>
    );
};

const getStarSVGInfo = (
    rating: number | null,
    starIndex: number,
    starHasStroke: boolean,
): StarSVGInfo => {
    const fractionalPart = rating ? modulo(rating, 1) : 0;
    const showHalfStar =
        fractionalPart >= HALF_STAR_MIN && fractionalPart < HALF_STAR_MAX;

    let svgPath = iconStar;
    let halfOutline = false;
    let outline = false;

    if (rating === null || rating === undefined || Number.isNaN(rating)) {
        // If rating is null, undefined or NaN, always show hollow stars
        svgPath = iconStarOutline;
        outline = starHasStroke;
    } else if (rating < starIndex) {
        if (rating > starIndex - 1) {
            // Check if current star is a half star...
            if (showHalfStar) {
                svgPath = iconStarHalfOutline;
                halfOutline = starHasStroke;
            } else if (fractionalPart < HALF_STAR_MIN) {
                svgPath = iconStarOutline;
                outline = starHasStroke;
            }
        } else {
            svgPath = iconStarOutline;
            outline = starHasStroke;
        }
    }

    // Do not change halfStar svgPath
    if (starHasStroke && !showHalfStar) {
        svgPath = iconStarWithStroke;
    }

    return { svgPath, halfOutline, outline };
};

const buildStarRatingClass = (
    cardClass: string,
    cardSize: string,
    starRatingClass?: string,
): string => {
    const propStarRatingClass = starRatingClass ? starRatingClass : "false";

    return classNames({
        "star-rating": true,
        [cardClass + "__star-rating"]: true,
        [cardClass + "--" + cardSize + "__star-rating"]: true,
        [propStarRatingClass]: !!starRatingClass,
        [styles.starRating]: cardClass === "star-rating",
    });
};

const buildStarRatingNumberClass = (cardClass: string): string => {
    return classNames({
        "star-rating__number": true,
        [cardClass + "__star-rating__number"]: true,
    });
};

const buildReviewNumberClass = (
    cardClass: string,
    reviewNumberClass?: string,
): string => {
    const propReviewNumberClass = reviewNumberClass
        ? reviewNumberClass
        : "false";

    return classNames({
        "star-rating__review-number": true,
        [cardClass + "__star-rating__review-number"]: true,
        [propReviewNumberClass]: !!reviewNumberClass,
    });
};

const renderStars = (
    rating: number | null,
    cardClass: string,
    starHasStroke: boolean,
): React.ReactNode[] => {
    const options = range(5);

    return options.map((starIndex) => {
        const { svgPath, halfOutline, outline } = getStarSVGInfo(
            rating,
            starIndex,
            starHasStroke,
        );

        const fractionalPart = rating ? modulo(rating, 1) : 0;
        const showHalfStar =
            fractionalPart >= HALF_STAR_MIN && fractionalPart < HALF_STAR_MAX;

        const starClass = classNames({
            "star-rating__star": true,
            "star-rating__star--half-outline": halfOutline && !showHalfStar,
            "star-rating__star--outline": outline,
            [cardClass + "__star"]: true,
        });

        return (
            <SVG
                key={starIndex}
                className={starClass}
                src={svgPath}
                aria-hidden="true"
            />
        );
    });
};

const renderReviewScreenReaderText = (
    rating: number | null,
): React.ReactNode => {
    return (
        <span className="ada-screenreader-only">
            {c("rating-graphic").t`Rated ${rating} out of 5 stars`}
        </span>
    );
};

const renderStarRatingElement = (
    stars: React.ReactNode[],
    reviewScreenReaderText: React.ReactNode,
    rating: number | null,
    numReviews: number | null | undefined,
    onlyReviewsNum: boolean | undefined,
    onlyStars: boolean | undefined,
    starHasStroke: boolean | undefined,
    reviewNumberClass: string,
    starRatingNumberClass: string,
): React.ReactNode => {
    if (onlyStars) {
        return (
            <div className="star-rating__stars">
                {reviewScreenReaderText}
                {stars}
            </div>
        );
    }

    if (numReviews !== undefined && numReviews !== null) {
        if (!onlyReviewsNum) {
            const reviewCountLabel = ngettext(
                msgid`Review`,
                `Reviews`,
                numReviews,
            );
            return (
                <>
                    <div className="star-rating__stars">
                        {reviewScreenReaderText}
                        {stars}
                    </div>
                    <div className={reviewNumberClass}>
                        {formatNumber(numReviews)} {reviewCountLabel}
                    </div>
                </>
            );
        } else {
            return (
                <>
                    <div className="star-rating__stars">
                        {reviewScreenReaderText}
                        {stars}
                    </div>
                    <div className={reviewNumberClass}>
                        {formatNumber(numReviews)}
                    </div>
                </>
            );
        }
    }

    return (
        <>
            <div className="star-rating__stars">
                {reviewScreenReaderText}
                {stars}
            </div>
            <div className={starRatingNumberClass}>{rating}</div>
            {starHasStroke ? buildLinearGradient() : null}
        </>
    );
};

export const RatingGraphic = (props: {
    cardClass: string;
    cardSize: string;
    rating: number | null;
    numReviews?: number | null;
    onlyReviewsNum?: boolean;
    onlyStars?: boolean;
    starHasStroke?: boolean;
    starRatingClass?: string;
    reviewNumberClass?: string;
}): React.ReactNode => {
    const starRatingClass = buildStarRatingClass(
        props.cardClass,
        props.cardSize,
        props.starRatingClass,
    );

    const starRatingNumberClass = buildStarRatingNumberClass(props.cardClass);

    const reviewNumberClass = buildReviewNumberClass(
        props.cardClass,
        props.reviewNumberClass,
    );

    const stars = renderStars(
        props.rating,
        props.cardClass,
        props.starHasStroke ?? false,
    );

    const reviewScreenReaderText = renderReviewScreenReaderText(props.rating);

    const starRatingElem = renderStarRatingElement(
        stars,
        reviewScreenReaderText,
        props.rating,
        props.numReviews,
        props.onlyReviewsNum,
        props.onlyStars,
        props.starHasStroke,
        reviewNumberClass,
        starRatingNumberClass,
    );

    return <div className={starRatingClass}>{starRatingElem}</div>;
};
